import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CaseIntelligence() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimIdFromUrl = searchParams.get('claim_id');

  const [claims, setClaims] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState(claimIdFromUrl || '');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [claim, setClaim] = useState(null);
  const [evidence, setEvidence] = useState([]);

  useEffect(() => {
    fetchClaims();
    if (claimIdFromUrl) {
      setSelectedClaimId(claimIdFromUrl);
      loadClaimDetails(claimIdFromUrl);
    }
  }, [claimIdFromUrl]);

  const fetchClaims = async () => {
    try {
      const response = await axios.get(`${API}/claims`);
      setClaims(response.data);
    } catch (error) {
      console.error('Error fetching claims:', error);
    }
  };

  const loadClaimDetails = async (claimId) => {
    setLoading(true);
    try {
      // Get claim
      const claimResponse = await axios.get(`${API}/claims/${claimId}`);
      setClaim(claimResponse.data);

      // Get evidence
      const evidenceResponse = await axios.get(`${API}/claims/${claimId}/evidence`);
      setEvidence(evidenceResponse.data);

      // Try to get existing report
      try {
        const reportResponse = await axios.get(`${API}/reports/${claimId}`);
        setReport(reportResponse.data);
      } catch (err) {
        // Report doesn't exist yet
        setReport(null);
      }
    } catch (error) {
      console.error('Error loading claim details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimSelect = (claimId) => {
    setSelectedClaimId(claimId);
    setReport(null);
    loadClaimDetails(claimId);
  };

  const generateReport = async () => {
    if (!selectedClaimId) {
      alert('Please select a claim');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/claims/${selectedClaimId}/analyze`);
      setReport(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 75) return 'text-teal-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Verified';
    if (score >= 75) return 'Strong';
    if (score >= 60) return 'Moderate';
    return 'Unverified';
  };

  const getScoreBgColor = (score) => {
    if (score >= 90) return 'from-green-500/10 to-green-900/10 border-green-500/30';
    if (score >= 75) return 'from-teal-500/10 to-teal-900/10 border-teal-500/30';
    if (score >= 60) return 'from-yellow-500/10 to-yellow-900/10 border-yellow-500/30';
    return 'from-red-500/10 to-red-900/10 border-red-500/30';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-slate-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors">
              ← Back
            </button>
            <div className="flex items-center space-x-3">
              <img src="/veritascore-logo.png" alt="VeritasCore" className="h-10 w-10" />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">VERITASCORE.IO</h1>
                <p className="text-xs text-teal-400">Case Intelligence — Powered by VeritasCore</p>
              </div>
            </div>
          </div>
          <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-white mb-6">VeritasCore Verification Engine</h2>
          <p className="text-gray-300 mb-2">Scores the credibility of real-world events with transparent, explainable analysis.</p>
          <p className="text-xs text-teal-400 mb-8">Generate Veritas Score™ and detailed reasoning</p>

          {/* Select Claim */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-300 mb-2">Select Claim to Analyze</label>
            <select
              value={selectedClaimId}
              onChange={(e) => handleClaimSelect(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="claim-select"
            >
              <option value="">Choose a claim...</option>
              {claims.map(c => (
                <option key={c.id} value={c.id}>
                  {c.title} - Status: {c.status}
                </option>
              ))}
            </select>
          </div>

          {/* Claim Details */}
          {claim && !loading && (
            <div className="mb-8 bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-xl font-bold text-white mb-4">Claim Details</h3>
              <div className="space-y-2 text-gray-300">
                <p><span className="font-semibold">Type:</span> {claim.incident_type}</p>
                <p><span className="font-semibold">Title:</span> {claim.title}</p>
                <p><span className="font-semibold">Statement:</span> {claim.raw_transcript || claim.raw_text || 'N/A'}</p>
                <p><span className="font-semibold">Evidence:</span> {evidence.length} piece(s) uploaded</p>
                <p><span className="font-semibold">Status:</span> {claim.status}</p>
              </div>
            </div>
          )}

          {/* Generate Report Button */}
          {selectedClaimId && !report && !analyzing && (
            <button
              onClick={generateReport}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-4 rounded-lg transition-all mb-8"
              data-testid="generate-report-btn"
            >
              Generate Veritas Score
            </button>
          )}

          {/* Analyzing State */}
          {analyzing && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mb-4"></div>
              <p className="text-xl text-white font-semibold">Analyzed by VeritasCore</p>
              <p className="text-gray-400 mt-2">Evaluating statement consistency, evidence alignment, and credibility factors...</p>
            </div>
          )}

          {/* Report Display */}
          {report && !analyzing && (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="bg-gradient-to-r from-teal-500/10 to-blue-500/10 border border-teal-500/30 rounded-2xl p-6">
                <div className="flex items-center space-x-4 mb-3">
                  <img src="/veritascore-logo.png" alt="VeritasCore" className="h-12 w-12" />
                  <div>
                    <h3 className="text-2xl font-bold text-white">VeritasCore™ Verified Report</h3>
                    <p className="text-gray-400 text-sm">Generated using VeritasCore™ verification and analysis system</p>
                  </div>
                </div>
              </div>

              {/* Veritas Score - Main Display */}
              <div className={`bg-gradient-to-br ${getScoreBgColor(report.veritas_score || report.credibility_score || 0)} border rounded-2xl p-8`}>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-300 mb-4">Veritas Score™</h3>
                  <div className="relative inline-block">
                    <div className="w-40 h-40 rounded-full border-8 border-white/10 flex items-center justify-center">
                      <div className="text-center">
                        <div className={`text-5xl font-bold ${getScoreColor(report.veritas_score || report.credibility_score || 0)}`}>
                          {report.veritas_score || report.credibility_score || 0}
                        </div>
                        <div className="text-sm text-gray-400">out of 100</div>
                      </div>
                    </div>
                  </div>
                  <p className={`text-xl font-semibold mt-4 ${getScoreColor(report.veritas_score || report.credibility_score || 0)}`}>
                    {report.classification || getScoreLabel(report.veritas_score || report.credibility_score || 0)}
                  </p>
                </div>
              </div>

              {/* Score Breakdown */}
              {report.statement_consistency !== undefined && (
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-2">Score Breakdown — Explainability</h3>
                  <p className="text-xs text-gray-400 mb-6">Why this number? Transparent reasoning for each component.</p>
                  <div className="space-y-6">
                    {/* Statement Consistency */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-300 font-semibold">1. Statement Consistency</span>
                        <span className="text-white font-bold">{report.statement_consistency || 0}/30</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                        <div className="bg-teal-400 h-2 rounded-full" style={{width: `${((report.statement_consistency || 0) / 30) * 100}%`}}></div>
                      </div>
                      {report.statement_consistency_reason && (
                        <p className="text-sm text-teal-300 bg-teal-500/10 rounded px-3 py-2 mt-2">
                          → {report.statement_consistency_reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Narrative logic and internal coherence</p>
                    </div>

                    {/* Evidence Alignment */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-300 font-semibold">2. Evidence Alignment</span>
                        <span className="text-white font-bold">{report.evidence_alignment || 0}/25</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                        <div className="bg-teal-400 h-2 rounded-full" style={{width: `${((report.evidence_alignment || 0) / 25) * 100}%`}}></div>
                      </div>
                      {report.evidence_alignment_reason && (
                        <p className="text-sm text-teal-300 bg-teal-500/10 rounded px-3 py-2 mt-2">
                          → {report.evidence_alignment_reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Photos and evidence support statement</p>
                    </div>

                    {/* Time Integrity */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-300 font-semibold">3. Time Integrity</span>
                        <span className="text-white font-bold">{report.time_integrity || 0}/15</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                        <div className="bg-teal-400 h-2 rounded-full" style={{width: `${((report.time_integrity || 0) / 15) * 100}%`}}></div>
                      </div>
                      {report.time_integrity_reason && (
                        <p className="text-sm text-teal-300 bg-teal-500/10 rounded px-3 py-2 mt-2">
                          → {report.time_integrity_reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Timestamp accuracy and sequence logic</p>
                    </div>

                    {/* Location Verification */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-300 font-semibold">4. Location Verification</span>
                        <span className="text-white font-bold">{report.location_verification || 0}/15</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                        <div className="bg-teal-400 h-2 rounded-full" style={{width: `${((report.location_verification || 0) / 15) * 100}%`}}></div>
                      </div>
                      {report.location_verification_reason && (
                        <p className="text-sm text-teal-300 bg-teal-500/10 rounded px-3 py-2 mt-2">
                          → {report.location_verification_reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">GPS and geographic consistency</p>
                    </div>

                    {/* User Trust History */}
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-300 font-semibold">5. User Trust History</span>
                        <span className="text-white font-bold">{report.user_trust_history || 0}/15</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                        <div className="bg-teal-400 h-2 rounded-full" style={{width: `${((report.user_trust_history || 0) / 15) * 100}%`}}></div>
                      </div>
                      {report.user_trust_history_reason && (
                        <p className="text-sm text-teal-300 bg-teal-500/10 rounded px-3 py-2 mt-2">
                          → {report.user_trust_history_reason}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Baseline trust scoring</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">Incident Summary</h3>
                <p className="text-gray-300">{report.incident_summary}</p>
              </div>

              {/* Consistency Analysis */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">Consistency Analysis</h3>
                <p className="text-gray-300">{report.consistency_analysis}</p>
              </div>

              {/* Key Findings */}
              {report.key_findings && (
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-3">Key Findings</h3>
                  <div className="text-gray-300 whitespace-pre-wrap">{report.key_findings}</div>
                </div>
              )}

              {/* Uncertainty Notes */}
              {report.uncertainty_notes && (
                <div className="bg-yellow-500/10 rounded-xl p-6 border border-yellow-500/30">
                  <h3 className="text-xl font-bold text-yellow-400 mb-3">Uncertainty Notes</h3>
                  <p className="text-gray-300">{report.uncertainty_notes}</p>
                </div>
              )}

              {/* Recommended Next Step */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">Recommended Next Step</h3>
                <p className="text-gray-300">{report.recommended_next_step}</p>
              </div>

              {/* Full Report */}
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-3">Professional Report</h3>
                <div className="text-gray-300 whitespace-pre-wrap">{report.report_text}</div>
              </div>

              {/* Report Footer */}
              <div className="bg-gradient-to-r from-teal-500/10 to-blue-500/10 border border-teal-500/30 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-400">This report was generated using VeritasCore™ verification and analysis system.</p>
              </div>

              {/* Actions */}
              <div className="flex space-x-4">
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-lg transition-all"
                  data-testid="print-report-btn"
                >
                  Print Report
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white font-semibold py-3 rounded-lg transition-all"
                  data-testid="back-to-dashboard-btn"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}